class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event, undefined, this.dataset.variantId, this.dataset.key);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`) || this.querySelector(`[data-index="${id}"]`);
    if (!input) return;
    const attrValue = input.getAttribute('value');
    // If the attribute isn't present (or was removed by an update), fall back to current value.
    if (attrValue != null) input.value = attrValue;
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  async resolveCurrentLine(lineItemKey, variantId) {
    try {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      const items = Array.isArray(cart.items) ? cart.items : [];

      let index = -1;
      if (lineItemKey) {
        index = items.findIndex((item) => item && item.key === lineItemKey);
      }
      if (index < 0 && variantId) {
        index = items.findIndex((item) => String(item && item.variant_id) === String(variantId));
      }
      return index >= 0 ? index + 1 : null;
    } catch (error) {
      return null;
    }
  }

  updateQuantity(line, quantity, event, name, variantId, lineItemKey) {
    this.enableLoading(line);
    let cartUpdatedSuccessfully = false;

    const sectionIds = this.getSectionsToRender().map((section) => section.section);
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const isRemove = Number(quantity) === 0;
    const errorsElement = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errorsElement) errorsElement.textContent = '';

    const postJson = async (url, payload) => {
      const response = await fetch(`${url}`, { ...fetchConfig(), ...{ body: JSON.stringify(payload) } });
      const text = await response.text();
      // Shopify usually returns JSON; on some failures it can return HTML. Treat non-JSON as a hard error.
      try {
        return JSON.parse(text);
      } catch (e) {
        const err = new Error('Non-JSON response from cart endpoint');
        err.status = response.status;
        err.body = text;
        throw err;
      }
    };

    Promise.resolve()
      .then(async () => {
        // Prefer key-based mutations when possible to avoid "line parameter is invalid" errors caused by stale line indexes.
        if (lineItemKey && isRemove) {
          const byKeyPayload = {
            id: lineItemKey,
            quantity,
            sections: sectionIds,
            sections_url: window.location.pathname,
          };
          const parsedState = await postJson(routes.cart_change_url, byKeyPayload);
          return { parsedState, effectiveLine: line };
        }

        // Fallback: line-based change (Dawn default).
        let effectiveLine = line;
        if (isRemove) {
          const resolvedLine = await this.resolveCurrentLine(lineItemKey, variantId);
          if (resolvedLine) effectiveLine = resolvedLine;
        }
        const byLinePayload = {
          line: effectiveLine,
          quantity,
          sections: sectionIds,
          sections_url: window.location.pathname,
        };
        let parsedState = await postJson(routes.cart_change_url, byLinePayload);

        // If line-based remove failed and we have a key, retry once using the key.
        if (parsedState && parsedState.errors && isRemove && lineItemKey) {
          const retryByKeyPayload = {
            id: lineItemKey,
            quantity,
            sections: sectionIds,
            sections_url: window.location.pathname,
          };
          parsedState = await postJson(routes.cart_change_url, retryByKeyPayload);
          return { parsedState, effectiveLine: line };
        }

        return { parsedState, effectiveLine };
      })
      .then(async (state) => {
        const parsedState = state.parsedState;
        const effectiveLine = state.effectiveLine;
        const quantityElement =
          document.getElementById(`Quantity-${effectiveLine}`) || document.getElementById(`Drawer-quantity-${effectiveLine}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(effectiveLine, parsedState.errors);
          return;
        }

        cartUpdatedSuccessfully = true;

        try {
          CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
            this.classList.toggle('is-empty', parsedState.item_count === 0);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            const cartFooter = document.getElementById('main-cart-footer');

            if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
            if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

            this.getSectionsToRender().forEach((section) => {
              const sectionRoot = document.getElementById(section.id);
              if (!sectionRoot || !parsedState.sections || !parsedState.sections[section.section]) return;
              const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
              if (!elementToReplace) return;
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            });
            const updatedValue = parsedState.items[effectiveLine - 1] ? parsedState.items[effectiveLine - 1].quantity : undefined;
            let message = '';
            if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
              if (typeof updatedValue === 'undefined') {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
              }
            }
            this.updateLiveRegions(effectiveLine, message);

            const lineItem =
              document.getElementById(`CartItem-${effectiveLine}`) || document.getElementById(`CartDrawer-Item-${effectiveLine}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }
          });
        } catch (renderError) {
          console.warn('[cart] non-fatal render error after successful update', renderError);
        }

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        document.dispatchEvent(
          new CustomEvent('soundtrap:cart-updated', {
            detail: { cartData: parsedState, variantId: variantId },
          })
        );
      })
      .catch((error) => {
        if (cartUpdatedSuccessfully) {
          console.warn('[cart] suppressed post-update error after successful cart mutation', error);
          document.dispatchEvent(new CustomEvent('soundtrap:cart-updated'));
          return;
        }
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
